import React, { useCallback, useMemo } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { useOrganization } from 'contexts/OrganizationsContext';

// Icons
import CaretDownIcon from 'assets/icons/triangle.svg?react';
import SearchIcon from 'assets/icons/search.svg?react';

import { AuthorizedView, Roles } from 'components/Utilities/AuthorizedView';
import Toggle from 'components/AGForm/Inputs/Toggle/Toggle';
import SearchSelectInput from 'components/AGForm/Inputs/SelectInput/SearchSelectInput';
import AGSimpleSelect, { SelectOption } from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import AGForm from 'components/AGForm/AGForm';
import styles from './Sidebar.module.css';
import { Organization, SpotInterface, SpotMetadata } from 'types';

type SidebarControlsProps = {
    spotsLoading: boolean;
    spotList: SpotMetadata[] | null;
    superuser: boolean;
    organization: Organization | null;
    isDemoMode: boolean;
    updateIsDemoMode: (isDemoMode: boolean) => void;
    navigateToTab: (tabUrl: string) => void;
    setIsExpanded: (isExpanded: boolean) => void;
    isExpanded: boolean;
    orgUrl: string;
    selectedSpot: SpotInterface | null;
    orgSpotsList: SelectOption[];
};

function SidebarControls({
    spotsLoading,
    spotList,
    superuser,
    organization,
    isDemoMode,
    updateIsDemoMode,
    navigateToTab,
    setIsExpanded,
    isExpanded,
    orgUrl,
    selectedSpot,
    orgSpotsList,
}: SidebarControlsProps) {
    const { organizations } = useOrganization();

    const allOrgsList = useMemo(
        () =>
            organizations
                ? organizations.map((org) => ({ label: org.name, value: `/organization/${org.pk}`, key: org.pk }))
                : [],
        [organizations]
    );

    const allSpotsList = useMemo(() => {
        return spotList
            ? spotList.map((spot) => ({
                  label: spot.name,
                  value: `/organization/${spot.organization}/spots/${spot.pk}/detail`,
                  key: spot.pk,
              }))
            : [];
    }, [spotList]);

    const showFormControls = superuser || orgSpotsList.length > 1;

    const routeToMatch = useMemo(() => {
        return selectedSpot ? '/organization/:orgId/spots/:spotId/:path' : '/organization/:orgId/:path';
    }, [selectedSpot]);

    const match = useRouteMatch<{
        orgId: string;
        spotId: string;
        path: string;
    }>(routeToMatch);

    const { search } = useLocation();

    const expandSidebar = useCallback(() => {
        setIsExpanded(true);
    }, []);

    const onChangeOrgSpot = useCallback(
        (spotId: string) => {
            if (!match || !organization) return;
            if (!match.params.path) {
                navigateToTab(`${orgUrl}spots/${spotId}/detail`);
            } else {
                // keep all search parameters except page - that could throw a 404.
                const query = new URLSearchParams(search);
                query.delete('page');
                navigateToTab(`${orgUrl}spots/${spotId}/${match.params.path}?${query.toString()}`);
            }
        },
        [match, organization, orgUrl, navigateToTab, search]
    );

    if (!showFormControls) return <></>;

    return (
        <AGForm>
            {superuser && (
                <>
                    {isExpanded ? (
                        <SearchSelectInput
                            label={undefined}
                            placeholder="Enter Org or Spot"
                            options={[
                                { label: 'Organizations', options: allOrgsList },
                                { label: 'Spots', options: allSpotsList },
                            ]}
                            errors={null}
                            onSelected={(value) => {
                                navigateToTab(value);
                            }}
                            className={isDemoMode ? 'blur' : ''}
                            name="organizationOrSpot"
                            disabled={spotsLoading}
                        />
                    ) : (
                        <button type="button" className={styles.collapsedButton} onClick={expandSidebar} title="Search">
                            <SearchIcon />
                        </button>
                    )}
                </>
            )}
            {orgSpotsList.length > 1 && (
                <>
                    {isExpanded ? (
                        <AGSimpleSelect
                            placeholder="Select property"
                            selected={String(selectedSpot?.pk) ?? ''}
                            onSelect={onChangeOrgSpot}
                            options={orgSpotsList}
                            maxHeight="420px"
                            width="100%"
                            className={isDemoMode ? 'blur' : ''}
                        />
                    ) : (
                        <button
                            type="button"
                            className={styles.collapsedButton}
                            onClick={expandSidebar}
                            title="Select property"
                        >
                            <CaretDownIcon />
                        </button>
                    )}
                </>
            )}

            {/* Demo toggle */}
            <AuthorizedView allowed={Roles.Superuser}>
                <div className={styles.toggleWrapper}>
                    <Toggle
                        id="demo-mode-toggle"
                        label={isExpanded ? 'Demo mode' : undefined}
                        checked={isDemoMode}
                        onChange={() => updateIsDemoMode(!isDemoMode)}
                    />
                </div>
            </AuthorizedView>
        </AGForm>
    );
}

export default SidebarControls;
